import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { Box } from '@mui/material';
import { debounce } from 'lodash';
import {
  clearCompanyList,
  fetchCompanyList,
  updateCompany,
} from 'redux/actions/companyAction';
import { getServiceCity } from 'redux/actions/UserServiceCity';
import { onLoaderTrigger } from 'redux/actions/commonAction';
import CommonSubHeader from 'components/commonSubHeader/CommonSubHeader';
import CommonTable from 'components/commonTable/CommonTable';
import Loader from 'components/loader/Loader';
import { companyFilterData } from './CompanyData';
import { handlePermissions } from 'utils/utils';
import { roleAndPermissionEnum } from 'utils/rolesPermissionsEnum';

const Company = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [searchParams, setSearchParams] = useSearchParams();
  const pageParams = searchParams?.get('page');
  const pageSizeParams = searchParams?.get('pageSize');
  const statusParams = searchParams?.get('status');
  const locationParams = searchParams?.get('location');
  const searchVal = searchParams?.get('search');
  let paramsList = {};
  searchParams.forEach((value, key) => {
    paramsList[key] = value;
  });
  const companyRoleEnum = roleAndPermissionEnum.company;

  const { state } = useLocation();
  const [page, setPage] = useState(
    pageParams ? parseInt(pageParams) : state?.payload?.page || 0
  );
  const [rowsPerPage, setRowsPerPage] = useState(
    pageSizeParams ? parseInt(pageSizeParams) : state?.payload?.pageSize || 10
  );
  const [status, setStatus] = useState(
    statusParams ? statusParams?.split(',') : state?.payload?.status || []
  );
  const [location, setLocation] = useState(
    locationParams ? locationParams.split(',') : state?.payload?.location || []
  );
  const [search, setSearch] = useState(
    searchVal || state?.payload?.search || ''
  );

  const profileData = JSON?.parse(localStorage.getItem('user'));
  const role = localStorage.getItem('role');
  const locationData = profileData?.Company?.ServiceCity?.name;

  let companyListPayload = {
    search: search,
    page: page,
    pageSize: rowsPerPage,
    status: status,
    location: role !== 'adminCompany' ? locationData : location,
  };

  useEffect(() => {
    // Dispatch the fetchCompanyList action with the updated payload
    dispatch(fetchCompanyList(companyListPayload));
    dispatch(getServiceCity());
  }, [state]);

  useEffect(() => {
    dispatch(onLoaderTrigger(false));
  }, []);

  useEffect(() => {
    return () => {
      dispatch(clearCompanyList());
    };
  }, []);

  const companyTableData = useSelector(
    (state) => state?.userCompany?.companyList
  );
  const tableLoader = useSelector((state) => state?.userCompany?.loading);

  const cityList = useSelector(
    (state) => state?.userServiceCity?.userServiceCity?.data
  );

  const cityOption = cityList?.map((val, idx) => {
    return { value: val?.name, key: 'Location' };
  });

  const pageNavigation = () => {
    navigate('/companies/new-booking');
  };

  const handleChangePage = (event, newPage) => {
    window.history.replaceState({}, '');
    setSearchParams({ ...paramsList, page: newPage });
    setPage(newPage);
    dispatch(fetchCompanyList({ ...companyListPayload, page: newPage }));
  };

  const handleChangeRowsPerPage = (event) => {
    window.history.replaceState({}, '');
    setSearchParams({
      ...paramsList,
      page: 0,
      pageSize: parseInt(event.target.value),
    });
    setRowsPerPage(parseInt(event.target.value));
    setPage(0);
    dispatch(
      fetchCompanyList({
        ...companyListPayload,
        page: 0,
        pageSize: parseInt(event.target.value),
      })
    );
  };

  const tableOnClickRow = (navigate, val) => {
    navigate(`/companies/companyprofile/${val.id}`, {
      state: { name: val.companyName, payload: companyListPayload },
    });
  };

  let formData = new FormData();
  const onClickMoreIcon = (value, type) => {
    if (type === 'Edit') {
      navigate('/companies/new-booking', {
        state: { edit: true, companyProfile: value },
      });
    } else {
      if (type === 'Inactive') {
        formData.append('id', value.id);
        formData.append('status', 'InActive');
      } else if (type === 'Active') {
        formData.append('id', value.id);
        formData.append('status', 'Active');
      }
      dispatch(
        updateCompany(formData, {
          pageType: 'companyListPage',
          companyListPayload: companyListPayload,
        })
      );
    }
  };
  //Filter Reset
  const onFilterChange = (val) => {
    window.history.replaceState({}, '');
    const defaultStatus = ['Active', 'InActive', 'Blocked'];
    const filterStatus = val.filter((status) => {
      return defaultStatus?.includes(status);
    });

    const defaultLocation = cityList?.map((val, idx) => {
      return val?.name;
    });
    const filterLocations = val.filter((location) => {
      return defaultLocation?.includes(location);
    });
    setSearchParams({
      ...paramsList,
      status: filterStatus?.toString(),
      location: filterLocations?.toString(),
      page: 0,
    });

    setLocation(filterLocations);
    setStatus(filterStatus);

    const companyFilterData = {
      ...companyListPayload,
      status: filterStatus?.toString(),
      location: filterLocations?.toString(),
      page: 0,
    };
    dispatch(fetchCompanyList(companyFilterData));
  };

  // ---Reset Filter---
  const onRestFilter = () => {
    setSearchParams({ ...paramsList, status: '', location: '' });
    window.history.replaceState({}, '');
    dispatch(
      fetchCompanyList({
        ...companyListPayload,
        status: '',
        location: role !== 'adminCompany' ? locationData : '',
      })
    );
    setStatus([]);
    setLocation([]);
  };

  //search Api
  const onSearchQuery = debounce((val) => {
    setSearchParams({ ...paramsList, search: val, page: 0, pageSize: '' });
    window.history.replaceState({}, '');
    setSearch(val);
    setPage(0);
    companyListPayload = {
      ...companyListPayload,
      search: val,
      pageSize: '',
      page: 0,
    };
    dispatch(fetchCompanyList(companyListPayload));
  }, 500);

  //roles and permissions
  const roleAndPermissions = useSelector(
    (state) => state?.getRolesAndPermissions?.rolesAndPermissions
  );
  const accessForAdd = handlePermissions(
    roleAndPermissions,
    companyRoleEnum.moduleKey,
    { defaultAllAccess: companyRoleEnum.all, customAccess: companyRoleEnum.add }
  );
  const accessForOnlyView = handlePermissions(
    roleAndPermissions,
    companyRoleEnum.moduleKey,
    { customAccess: companyRoleEnum.view }
  );
  const accessForOnlyEdit = handlePermissions(
    roleAndPermissions,
    companyRoleEnum.moduleKey,
    { customAccess: companyRoleEnum.edit }
  );
  const accessForOnlyStatus = handlePermissions(
    roleAndPermissions,
    companyRoleEnum.moduleKey,
    { customAccess: companyRoleEnum.editStatus }
  );

  const viewMoreIcon = accessForAdd || accessForOnlyEdit || accessForOnlyStatus;

  return (
    <Box className="mb20">
      <Box className="mb30">
        <CommonSubHeader
          navigation={pageNavigation}
          buttonName={accessForAdd && 'Add Company'}
          onSearchQuery={onSearchQuery}
          onFilterChange={onFilterChange}
          onRestFilter={onRestFilter}
          defaultStatus={status}
          defaultLocation={location}
          filter={true}
          filterData={companyFilterData(cityOption)}
          tabName={'company'}
          search={search}
        />
      </Box>
      {tableLoader ? (
        <Loader height={'60vh'} />
      ) : (
        <CommonTable
          data={companyTableData}
          viewMoreIcon={!viewMoreIcon}
          companyTable={true}
          tableOnClickRow={tableOnClickRow}
          page={page}
          isClickable={true}
          rowsPerPage={rowsPerPage}
          handleChangePage={handleChangePage}
          handleChangeRowsPerPage={handleChangeRowsPerPage}
          onClickMoreIcon={onClickMoreIcon}
          tabName={'company'}
        />
      )}
    </Box>
  );
};

export default Company;
